import { MoreVerticalIcon, StarIcon, StarOffIcon, TrashIcon, PlusIcon, MapPinIcon, BuildingIcon } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { Avatar } from '../shared/avatar'
import { ScoreRow } from '../shared/score-mini-chart'
import { useLingui } from '@lingui/react/macro'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Item, ItemContent, ItemTitle, ItemMedia, ItemActions } from '@/components/ui/item'
import { useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { toast } from '@/lib/toast'
import { queryClient, trpc } from '@/main'
import { useRootSearch } from '@/hooks/use-root-search'
import { StarIcon as StarIconFilled } from '@/routes/_app/-components/assets/star-icon'
import type { SelectOption } from '../shared/select-by-buttons'

interface Offer {
  id: number
  name: string | null
  image: string | null
  company: string | null
  city?: string | null
  province?: string | null
  matchPercentage: number
  matchPercentageValues: number
  matchPercentagePersonality: number
  matchPercentageGlobal: number
  added: boolean | null
  favorite: boolean
  described?: boolean
  describedValues?: boolean
  describedPersonality?: boolean
}

export function ApplicantOfferTreeItem(props: { offer: Offer; isSelected: boolean; currentSelectType?: SelectOption }) {
  const { t } = useLingui()
  const { data: user } = useSuspenseQuery(trpc.auth.getUser.queryOptions())
  const { o } = useRootSearch()

  // Each mutation confirms with a toast (matching the company-side offer flows in
  // offers/applicant-item.tsx) so the action has explicit, observable feedback — both for
  // the user and so flows that act-then-navigate have a settled signal to wait on.
  const { mutate: addOffer, isPending: isAdding } = useMutation(
    trpc.applicants.addOffer.mutationOptions({
      onSuccess: () => toast.success(t`Offer added successfully`),
      onError: () => toast.error(t`Failed to add offer`),
      onSettled: () => queryClient.invalidateQueries(trpc.applicants.getOffersInfinite.infiniteQueryFilter()),
    }),
  )

  const { mutate: removeOffer } = useMutation(
    trpc.applicants.removeOffer.mutationOptions({
      onSuccess: () => toast.success(t`Offer removed successfully`),
      onError: () => toast.error(t`Failed to remove offer`),
      onSettled: () => queryClient.invalidateQueries(trpc.applicants.getOffersInfinite.infiniteQueryFilter()),
    }),
  )

  const { mutate: setOfferFavorite } = useMutation(
    trpc.applicants.setOfferFavorite.mutationOptions({
      onSuccess: (_data, variables) =>
        toast.success(variables.isFavorite ? t`Added to favorites` : t`Removed from favorites`),
      onError: () => toast.error(t`Failed to update favorite`),
      onSettled: () => queryClient.invalidateQueries(trpc.applicants.getOffersInfinite.infiniteQueryFilter()),
    }),
  )

  const handleAdd = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    addOffer(props.offer.id)
  }

  const location = [props.offer.city, props.offer.province].filter(Boolean).join(', ')

  return (
    <Item asChild size="xs" isSelected={props.isSelected} className="group">
      <Link
        to="/offers/$offerId/report"
        params={{ offerId: props.offer.id.toString() }}
        search={(prev) => ({
          ...prev,
          first: { type: 'job', id: props.offer.id },
          second: { type: 'applicant', id: user?.id },
          selected: 'compare',
        })}
      >
        <ItemMedia>
          <div className="relative shrink-0">
            <Avatar size="lg" name={props.offer.name} url={props.offer.image} type="job" variant="job" />
            {props.offer.favorite && (
              <StarIconFilled
                role="img"
                aria-label={t`Favorite`}
                className="absolute -top-1.5 -right-1.5 size-4 text-(--color-favorite)"
              />
            )}
          </div>
        </ItemMedia>

        <ItemContent className="min-w-0 gap-1">
          <ItemTitle>{props.offer.name}</ItemTitle>
          {(props.offer.company || location) && (
            <div className="text-muted-foreground flex min-w-0 items-center gap-2.5 overflow-hidden text-xs">
              {props.offer.company && (
                <span className="flex min-w-0 items-center gap-1 truncate">
                  <BuildingIcon className="size-3 shrink-0" />
                  <span className="truncate">{props.offer.company}</span>
                </span>
              )}
              {location && (
                <span className="flex min-w-0 items-center gap-1 truncate">
                  <MapPinIcon className="size-3 shrink-0" />
                  <span className="truncate">{location}</span>
                </span>
              )}
            </div>
          )}
          <ScoreRow
            items={[
              {
                key: 'skills',
                kind: 'competences',
                passed: true,
                standard: props.offer.described !== true,
                score: props.offer.matchPercentage,
                label: t`Competences`,
              },
              {
                key: 'values',
                kind: 'values',
                passed: true,
                standard: props.offer.describedValues !== true,
                score: props.offer.matchPercentageValues,
                label: t`Values`,
              },
              {
                key: 'traits',
                kind: 'personality',
                passed: true,
                standard: props.offer.describedPersonality !== true,
                score: props.offer.matchPercentagePersonality,
                label: t`Personality`,
              },
            ]}
            activeKey={
              o === 'global'
                ? 'global'
                : !props.currentSelectType
                  ? undefined
                  : props.currentSelectType === 'competences'
                    ? 'skills'
                    : props.currentSelectType === 'values'
                      ? 'values'
                      : 'traits'
            }
            global={{ score: props.offer.matchPercentageGlobal, label: t`Global` }}
          />
        </ItemContent>

        <ItemActions>
          {!props.offer.added ? (
            <Button
              variant="ghost"
              size="icon"
              className="size-6 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
              onClick={handleAdd}
              disabled={isAdding}
            >
              <PlusIcon className="size-3.5" />
            </Button>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-6 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                >
                  <MoreVerticalIcon className="size-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={() => setOfferFavorite({ offerId: props.offer.id, isFavorite: !props.offer.favorite })}
                >
                  {props.offer.favorite ? (
                    <StarOffIcon className="mr-2 size-4" />
                  ) : (
                    <StarIcon className="mr-2 size-4" />
                  )}
                  {props.offer.favorite ? t`Unmark as favorite` : t`Mark as favorite`}
                </DropdownMenuItem>
                <DropdownMenuItem className="text-destructive" onSelect={() => removeOffer(props.offer.id)}>
                  <TrashIcon className="mr-2 size-4" />
                  {t`Remove`}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </ItemActions>
      </Link>
    </Item>
  )
}
